import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRoster, type TabKey } from '../lib/roster'
import {
  TAB_PATH,
  deleteView,
  describeView,
  loadSavedViews,
  saveView,
  type SavedView,
} from '../lib/views'

/**
 * Saved views: the same selection, one click, next time.
 *
 * Renee sets the same three or four selections over and over — the renewal
 * cohort, the people with no address, whichever team she is working through.
 * Nothing here is new capability; it is the same dropdowns, remembered.
 */
export default function ViewsMenu({ tab }: { tab: TabKey }) {
  const r = useRoster()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // Loaded up front, not on first open, or the button says "Views" with no
  // count until she has opened it once.
  const [views, setViews] = useState<SavedView[]>(() => loadSavedViews())
  const [name, setName] = useState('')
  const [failed, setFailed] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) setViews(loadSavedViews())
  }, [open])

  /**
   * A full scan of the roster per saved view, so it is worked out once per
   * change rather than on every keystroke in the name box.
   */
  const counts = useMemo(() => {
    const m = new Map<string, number>()
    if (open) for (const v of views) m.set(v.id, r.countFor(v))
    return m
  }, [open, views, r.agents, r.tagsByAgent])

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const current = r.currentView(name.trim() || 'Untitled', tab)
  const nothingSet =
    current.filters.length === 0 &&
    !current.search &&
    !current.include_departed &&
    current.tab === 'all'

  function save() {
    const label = name.trim()
    if (!label) return
    const result = saveView(r.currentView(label, tab))
    if (!result.stored) {
      setFailed('This browser is blocking storage, so the list could not be saved.')
      return
    }
    setFailed(null)
    setViews(result.views)
    setName('')
  }

  return (
    <div className="exportwrap" ref={box}>
      <button className="btn sm ghost" onClick={() => setOpen((v) => !v)}>
        Views{views.length > 0 ? ` · ${views.length}` : ''}
      </button>

      {open && (
        <div className="exportmenu wide">
          {views.length === 0 && (
            <p className="hint">
              Nothing saved yet. Set up a list the way you want it, give it a name below, and it
              will be one click from now on.
            </p>
          )}

          {views.map((v) => (
            <div className="savedview" key={v.id}>
              <button
                className="savedview-open"
                onClick={() => {
                  // Land on the tab it was saved from, the way Pearl's offers
                  // and the What's Missing rows do.
                  r.applyView(v)
                  navigate(TAB_PATH[v.tab] ?? '/')
                  setOpen(false)
                }}
              >
                <strong>{v.label}</strong>
                <span className="hint">
                  {describeView(v) || 'Everyone'} · {counts.get(v.id) ?? 0} agents
                </span>
              </button>
              <button
                className="savedview-x"
                title={`Forget "${v.label}"`}
                onClick={() => setViews(deleteView(v.id).views)}
              >
                ×
              </button>
            </div>
          ))}

          {failed && <p className="error">{failed}</p>}

          <div className="savebar">
            <input
              value={name}
              placeholder="Name this list…"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save()
              }}
            />
            <button className="btn sm" onClick={save} disabled={!name.trim() || nothingSet}>
              Save
            </button>
          </div>
          <p className="hint">
            {nothingSet
              ? 'Set a filter, a search or a different tab first — there is nothing to save yet.'
              : `Saves what is on screen now: ${describeView(current)}`}
          </p>
          <p className="hint">
            Saved lists live in this browser, so they are yours and they stay on this computer.
          </p>
        </div>
      )}
    </div>
  )
}
